import { inject, Injectable } from '@angular/core';
import { AppConfigModel, FormContentModel, PageModel } from '@libs/cms/models';
import { AuthService } from '@libs/shared/auth';
import { CmsLanguageService } from '@libs/shared/common';
import { FileInputValueModel, GroupValueType, InputValueType, isInputItem, StepItemModel } from '@libs/form/models';
import { NavItemModel } from '@libs/shared/models';
import { DateTime } from 'luxon';
import { Observable } from 'rxjs';
import { CmsRepositoryService } from './cms-repository.service';

export const SECTION_ID_DEFAULT: string = '10';

const CONTENT_CACHE_TTL_MS: number = 15 * 60 * 1000; // 15 minutes, set 0 to disable cache

interface ContentCacheEntry {
  readonly content$: Observable<PageModel>;
  readonly createdAt: number;
}

@Injectable({ providedIn: 'root' })
export class CmsService {
  private readonly repository: CmsRepositoryService = inject(CmsRepositoryService);
  private readonly cmsLanguageService: CmsLanguageService = inject(CmsLanguageService);
  private readonly authService: AuthService = inject(AuthService);

  private readonly contentByKey: Map<string, ContentCacheEntry> = new Map<string, ContentCacheEntry>();

  public getAppConfig(): Observable<AppConfigModel> {
    return this.repository.getAppConfig();
  }

  public getAppContent(contentPath: string): Observable<PageModel> {
    // The first page may be fetched again after the CMS determines its language.
    const language: string | undefined = this.cmsLanguageService.getRequestLanguage(contentPath);
    const customerNumber: string | undefined = this.authService.currentCustomer()?.id;
    const cacheKey: string = [customerNumber, contentPath, language].filter(Boolean).join('::');
    const now: number = Date.now();

    const cachedContent: ContentCacheEntry | undefined = this.contentByKey.get(cacheKey);

    if (cachedContent && now - cachedContent.createdAt < CONTENT_CACHE_TTL_MS) {
      return cachedContent.content$;
    }

    const content$: Observable<PageModel> = this.repository.getAppContent(contentPath);

    this.contentByKey.set(cacheKey, {
      content$,
      createdAt: now,
    });

    return content$;
  }

  public submitAppForm(formContent: FormContentModel, formValue: GroupValueType): Observable<undefined> {
    const form: StepItemModel = formContent.form;
    const formData: FormData = new FormData();

    form.items.forEach(item => {
      if (!isInputItem(item)) {
        return;
      }

      const name: string = item.name;
      const value: InputValueType = formValue[name];

      if (value === undefined || value === null) {
        return;
      }

      if (item.type === 'file' && Array.isArray(value)) {
        const files: FileInputValueModel[] = value as FileInputValueModel[];

        files.forEach(fileValue => {
          formData.append(name, fileValue.file, fileValue.file.name);
        });

        return;
      }

      if (item.type === 'date' && item.format && value instanceof DateTime) {
        formData.append(name, value.toFormat(item.format));

        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      const formattedValue: string = typeof value === 'object' ? JSON.stringify(value) : String(value);

      formData.append(name, formattedValue);
    });

    return this.repository.submitAppForm(form.name, formData);
  }

  // API v1
  public getPage(contentId: string | number, customerNumber?: string): Observable<PageModel> {
    return this.repository.getPage(SECTION_ID_DEFAULT, contentId, customerNumber);
  }

  public getStructure(contentId: string | number, customerNumber?: string): Observable<NavItemModel> {
    return this.repository.getStructure(contentId, customerNumber);
  }
}
